<template>
  <!-- When showing Template1/Template2, do NOT add rms-template-1/2 so the old CSS does not override the reference template styling -->
  <div
    class="rms-public public-restaurant-page"
    :class="data ? 'rms-public-vue' : templateClass"
    :style="wrapperStyle"
  >
    <!-- Loading -->
    <div v-if="loading" class="rms-public__loading">
      <span class="material-icons rms-public__spinner" aria-hidden="true">sync</span>
      <p class="rms-public__loading-text">Loading…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="rms-public__error">
      <img src="/logos/QRestoo-logo.png" alt="QRestoo Logo" class="block h-16 rms-public__error-icon">

      <div class="rms-public__error-card">
        <h1 class="rms-public__error-title">{{ t('public.restaurantNotFoundTitle') }}</h1>
        <p class="rms-public__error-message">{{ error }}</p>
        <a
          :href="landingPageUrl"
          class="rms-public__error-cta"
          data-testid="public-back-to-home"
        >
          <span class="material-icons rms-public__error-cta-icon" aria-hidden="true">arrow_back</span>
          {{ t('public.backToHome') }}
        </a>
      </div>
    </div>

    <template v-else-if="data">
      <!-- Announcement dialog (paid feature; shown for 4 days when active) -->
      <Teleport to="body">
        <div
          v-if="showAnnouncementDialog"
          class="rms-public-announcement-dialog"
          data-testid="public-announcement-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rms-public-announcement-title"
          aria-describedby="rms-public-announcement-desc"
        >
          <div
            class="rms-public-announcement-dialog__backdrop"
            aria-hidden="true"
            @click="closeAnnouncementDialog"
          />
          <div
            class="rms-public-announcement-dialog__card"
            :style="announcementDialogStyle"
            @click.stop
          >
            <div class="rms-public-announcement-dialog__header">
              <h2 id="rms-public-announcement-title" class="rms-public-announcement-dialog__title">
                <span class="material-icons rms-public-announcement-dialog__icon" aria-hidden="true">campaign</span>
                {{ t('public.announcementTitle') }}
              </h2>
              <button
                type="button"
                class="rms-public-announcement-dialog__close-btn"
                :aria-label="t('public.close')"
                @click="closeAnnouncementDialog"
              >
                <span class="material-icons" aria-hidden="true">close</span>
              </button>
            </div>
            <div class="rms-public-announcement-dialog__inner">
              <img
                v-if="data.active_announcement.image_url"
                :src="data.active_announcement.image_url"
                alt=""
                class="rms-public-announcement-dialog__image"
              >
              <div
                id="rms-public-announcement-desc"
                class="rms-public-announcement-dialog__message"
                role="region"
                aria-label="Announcement content"
              >
                {{ announcementPlainMessage }}
              </div>
              <div class="rms-public-announcement-dialog__checkbox-row">
                <input
                  id="rms-announcement-hide-4days"
                  v-model="hideAnnouncementForFourDays"
                  type="checkbox"
                  class="rms-public-announcement-dialog__checkbox"
                  :aria-describedby="'rms-announcement-hide-4days-desc'"
                >
                <label for="rms-announcement-hide-4days" :id="'rms-announcement-hide-4days-desc'" class="rms-public-announcement-dialog__checkbox-label">
                  {{ t('public.announcementHideForFourDays') }}
                </label>
              </div>
              <button
                type="button"
                class="rms-public-announcement-dialog__cta"
                :style="announcementButtonStyle"
                :aria-label="t('public.close')"
                @click="closeAnnouncementDialog"
              >
                {{ t('public.close') }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
      <Template1
        v-if="templateComponent === 'Template1'"
        :restaurant="effectiveRestaurant"
        :owner-view-mode="ownerViewMode"
        :languages="data.languages || []"
        :current-locale="locale"
        @select-locale="onSelectLocale"
      >
        <template #feedback-form>
          <section id="feedback" class="rms-feedback rms-feedback--template-1" aria-labelledby="feedback-heading">
            <h3 id="feedback-heading" class="rms-feedback__title">{{ t('public.leaveFeedback') }}</h3>
            <form
              novalidate
              class="rms-feedback__form"
              @submit.prevent="submitFeedback"
            >
              <p v-if="feedbackError" class="rms-feedback__error" role="alert">{{ feedbackError }}</p>
              <p v-if="feedbackSuccess" class="rms-feedback__success" role="status">{{ feedbackSuccess }}</p>

              <div class="rms-feedback__field">
                <label for="feedback-rating" class="rms-feedback__label">{{ t('public.ratingLabel') }} <span class="rms-feedback__required">*</span></label>
                <div class="rms-feedback__stars" role="group" aria-label="Rating 1 to 5 stars">
                  <button
                    v-for="r in 5"
                    :key="r"
                    type="button"
                    class="rms-feedback__star-btn"
                    :class="{ 'rms-feedback__star-btn--active': r <= feedbackRating }"
                    :aria-label="`${r} star${r > 1 ? 's' : ''}`"
                    :aria-pressed="feedbackRating === r"
                    @click="feedbackRating = r"
                  >
                    <span class="material-icons" aria-hidden="true">star</span>
                  </button>
                </div>
                <p v-if="fieldErrors.rating" class="rms-feedback__field-error">{{ fieldErrors.rating }}</p>
              </div>

              <div class="rms-feedback__field">
                <label for="feedback-name" class="rms-feedback__label">{{ t('public.yourName') }} <span class="rms-feedback__required">*</span></label>
                <input
                  id="feedback-name"
                  v-model="feedbackName"
                  type="text"
                  autocomplete="name"
                  maxlength="255"
                  class="rms-feedback__input"
                  :aria-invalid="!!fieldErrors.name"
                  :aria-describedby="fieldErrors.name ? 'feedback-name-error' : undefined"
                />
                <p id="feedback-name-error" v-if="fieldErrors.name" class="rms-feedback__field-error">{{ fieldErrors.name }}</p>
              </div>

              <div class="rms-feedback__field">
                <label for="feedback-text" class="rms-feedback__label">{{ t('public.yourMessage') }} <span class="rms-feedback__required">*</span></label>
                <textarea
                  id="feedback-text"
                  v-model="feedbackText"
                  rows="4"
                  maxlength="65535"
                  class="rms-feedback__input rms-feedback__textarea"
                  :aria-invalid="!!fieldErrors.text"
                  :aria-describedby="fieldErrors.text ? 'feedback-text-error' : undefined"
                />
                <p id="feedback-text-error" v-if="fieldErrors.text" class="rms-feedback__field-error">{{ fieldErrors.text }}</p>
              </div>

              <div v-if="recaptchaEnabled && !recaptchaIsV3" class="rms-feedback__field rms-feedback__recaptcha">
                <div ref="recaptchaContainerRef" class="rms-feedback__recaptcha-widget" aria-label="reCAPTCHA" />
                <p v-if="fieldErrors.captcha" class="rms-feedback__field-error" role="alert">{{ fieldErrors.captcha }}</p>
              </div>

              <button
                type="submit"
                class="rms-feedback__submit"
                :disabled="feedbackSubmitting"
              >
                {{ feedbackSubmitting ? '…' : t('public.sendFeedback') }}
              </button>
            </form>
          </section>
        </template>
      </Template1>
      <Template2
        v-else-if="templateComponent === 'Template2'"
        :restaurant="effectiveRestaurant"
        :owner-view-mode="ownerViewMode"
        :languages="data.languages || []"
        :current-locale="locale"
        @select-locale="onSelectLocale"
      >
        <template #feedback-form>
          <section id="feedback" class="rms-feedback rms-feedback--template-2 rms-feedback--t2-reference" aria-labelledby="feedback-heading-t2">
            <div class="absolute inset-0 opacity-5 pointer-events-none rms-feedback-t2__grid-bg" aria-hidden="true" />
            <div class="rms-feedback-t2__inner relative z-10 max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 border-x border-charcoal-blue">
              <div class="rms-feedback-t2__copy md:col-span-4 px-4 py-6 sm:px-6 md:p-12 lg:p-20 border-b md:border-b-0 md:border-r border-charcoal-blue bg-pale-stone/50">
                <p class="font-mono text-oxidized-copper text-sm mb-4 tracking-tighter uppercase font-bold">// {{ t('public.t2FeedbackPerformanceLogging') }}</p>
                <h2 id="feedback-heading-t2" class="break-words heading-utilitarian text-3xl md:text-5xl lg:text-5xl font-extrabold leading-none mb-6 md:mb-8 text-charcoal-blue">{{ t('public.t2FeedbackOperationalLog') }}</h2>
                <p class="text-charcoal-blue font-body text-sm font-bold uppercase tracking-widest leading-relaxed border-l-4 border-oxidized-copper pl-6">
                  {{ t('public.t2FeedbackTransmitCopy') }}
                </p>
                <div class="mt-8 md:mt-12 p-4 md:p-6 border border-charcoal-blue/20 bg-white/40">
                  <p class="font-mono text-[10px] uppercase opacity-60">{{ t('public.t2FeedbackSessionMeta') }}</p>
                  <p class="font-mono text-[10px] uppercase font-bold">{{ t('public.t2FeedbackTerminalId') }}</p>
                  <p class="font-mono text-[10px] uppercase font-bold">{{ t('public.t2FeedbackProtocol') }}</p>
                </div>
              </div>
              <div class="rms-feedback-t2__form-col md:col-span-8 px-4 py-6 sm:px-6 md:p-12 lg:p-20">
                <form
                  novalidate
                  class="rms-feedback-t2__form space-y-6 sm:space-y-8 md:space-y-10"
                  @submit.prevent="submitFeedback"
                >
                  <p v-if="feedbackError" class="rms-feedback__error rms-feedback-t2__error" role="alert">{{ feedbackError }}</p>
                  <p v-if="feedbackSuccess" class="rms-feedback__success rms-feedback-t2__success" role="status">{{ feedbackSuccess }}</p>

                  <div class="space-y-4">
                    <label class="heading-utilitarian text-lg md:text-xl font-extrabold block text-charcoal-blue">{{ t('public.t2FeedbackRatingLabel') }}</label>
                    <div class="rms-feedback-t2__rating-group flex flex-row-reverse justify-end gap-2" role="group" aria-label="Rating 1 to 5 stars">
                      <button
                        v-for="r in [5,4,3,2,1]"
                        :key="r"
                        type="button"
                        class="rms-feedback-t2__star-btn p-0 border-0 bg-transparent cursor-pointer transition-all duration-200 text-charcoal-blue hover:text-oxidized-copper min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-oxidized-copper focus-visible:ring-offset-2 rounded-none"
                        :class="{ 'text-oxidized-copper': r <= feedbackRating }"
                        :aria-label="`${r} star${r > 1 ? 's' : ''}`"
                        :aria-pressed="feedbackRating === r"
                        @click="feedbackRating = r"
                      >
                        <span class="material-icons text-4xl md:text-5xl" aria-hidden="true">{{ r <= feedbackRating ? 'star' : 'star_border' }}</span>
                      </button>
                    </div>
                    <p class="font-mono text-[10px] uppercase opacity-40">{{ t('public.t2FeedbackRatingHint') }}</p>
                    <p v-if="fieldErrors.rating" class="rms-feedback__field-error text-sm text-red-600 mt-1">{{ fieldErrors.rating }}</p>
                  </div>

                  <div class="space-y-4">
                    <label for="feedback-t2-name" class="heading-utilitarian text-lg md:text-xl font-extrabold block text-charcoal-blue">{{ t('public.yourName') }} <span class="text-red-600" aria-hidden="true">*</span></label>
                    <input
                      id="feedback-t2-name"
                      v-model="feedbackName"
                      type="text"
                      autocomplete="name"
                      maxlength="255"
                      class="rms-feedback-t2__input w-full min-h-[48px] bg-pale-stone border-2 border-charcoal-blue p-4 font-mono text-sm font-bold placeholder:text-charcoal-blue/20 text-charcoal-blue shadow-[4px_4px_0px_0px_rgba(30,41,59,1)] focus:ring-2 focus:ring-oxidized-copper focus:border-oxidized-copper focus:outline-none rounded-none"
                      :aria-invalid="!!fieldErrors.name"
                      :aria-describedby="fieldErrors.name ? 'feedback-t2-name-error' : undefined"
                    >
                    <p id="feedback-t2-name-error" v-if="fieldErrors.name" class="rms-feedback__field-error text-sm text-red-600">{{ fieldErrors.name }}</p>
                  </div>

                  <div class="space-y-4">
                    <div class="flex justify-between items-end">
                      <label for="feedback-t2-text" class="heading-utilitarian text-lg md:text-xl font-extrabold block text-charcoal-blue">{{ t('public.t2FeedbackSitrepLabel') }} <span class="text-red-600" aria-hidden="true">*</span></label>
                      <span class="font-mono text-[10px] opacity-40 uppercase">{{ feedbackText.length }}/65535</span>
                    </div>
                    <div class="relative">
                      <textarea
                        id="feedback-t2-text"
                        v-model="feedbackText"
                        rows="6"
                        maxlength="65535"
                        class="rms-feedback-t2__textarea w-full min-h-[12rem] bg-pale-stone border-2 border-charcoal-blue p-4 md:p-6 font-mono text-sm font-bold placeholder:text-charcoal-blue/20 resize-none shadow-[4px_4px_0px_0px_rgba(30,41,59,1)] focus:ring-2 focus:ring-oxidized-copper focus:border-oxidized-copper focus:outline-none rounded-none"
                        :placeholder="t('public.t2FeedbackPlaceholder')"
                        :aria-invalid="!!fieldErrors.text"
                        :aria-describedby="fieldErrors.text ? 'feedback-t2-text-error' : undefined"
                      />
                      <div class="absolute top-0 right-0 p-2 opacity-10 pointer-events-none" aria-hidden="true">
                        <span class="material-symbols-outlined text-4xl text-charcoal-blue">edit_note</span>
                      </div>
                    </div>
                    <p id="feedback-t2-text-error" v-if="fieldErrors.text" class="rms-feedback__field-error text-sm text-red-600">{{ fieldErrors.text }}</p>
                  </div>

                  <div v-if="recaptchaEnabled && !recaptchaIsV3" class="rms-feedback__field rms-feedback__recaptcha">
                    <div ref="recaptchaContainerRef" class="rms-feedback__recaptcha-widget" aria-label="reCAPTCHA" />
                    <p v-if="fieldErrors.captcha" class="rms-feedback__field-error text-sm text-red-600 mt-1" role="alert">{{ fieldErrors.captcha }}</p>
                  </div>

                  <div class="flex flex-col md:flex-row items-center gap-6 md:gap-8 pt-2 md:pt-4">
                    <button
                      type="submit"
                      class="rms-feedback-t2__submit w-full md:w-auto bg-oxidized-copper text-white px-8 md:px-12 py-4 md:py-5 heading-utilitarian font-extrabold text-xl md:text-2xl border-2 border-charcoal-blue shadow-[8px_8px_0px_0px_rgba(30,41,59,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all flex items-center justify-center gap-3 min-h-[48px] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-[8px_8px_0px_0px_rgba(30,41,59,1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2 rounded-none"
                      :disabled="feedbackSubmitting"
                    >
                      {{ feedbackSubmitting ? '…' : t('public.t2FeedbackSubmitReport') }}
                      <span class="material-symbols-outlined text-2xl md:text-3xl" aria-hidden="true">upload_file</span>
                    </button>
                    <p class="font-mono text-[10px] text-charcoal-blue/50 uppercase tracking-widest max-w-[200px]">
                      {{ t('public.t2FeedbackDisclaimer') }}
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </template>
      </Template2>
      <Template3
        v-else-if="templateComponent === 'Template3'"
        :restaurant="effectiveRestaurant"
        :owner-view-mode="ownerViewMode"
        :languages="data.languages || []"
        :current-locale="locale"
        @select-locale="onSelectLocale"
      >
        <template #feedback-form>
          <section id="feedback" class="rms-feedback rms-feedback--template-3 py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-t3-accent-dark/20" aria-labelledby="feedback-heading-t3">
            <div class="max-w-3xl mx-auto bg-t3-bg-dark border border-white/5 p-8 sm:p-10 rounded-2xl shadow-xl">
              <div class="text-center mb-8 sm:mb-10">
                <h2 id="feedback-heading-t3" class="text-2xl sm:text-3xl font-bold mb-2 tracking-tight text-slate-100">{{ t('public.t3ShareExperience') }}</h2>
                <p class="text-slate-400">{{ t('public.t3FeedbackHelps') }}</p>
              </div>
              <form novalidate class="space-y-6" @submit.prevent="submitFeedback">
                <p v-if="feedbackError" class="text-red-400 text-sm" role="alert">{{ feedbackError }}</p>
                <p v-if="feedbackSuccess" class="text-green-400 text-sm" role="status">{{ feedbackSuccess }}</p>
                <div class="flex justify-center gap-2">
                  <button
                    v-for="r in 5"
                    :key="r"
                    type="button"
                    class="p-1 min-h-[44px] min-w-[44px] text-t3-primary hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded"
                    :class="r > feedbackRating ? 'text-slate-500' : ''"
                    :aria-label="`${r} star${r > 1 ? 's' : ''}`"
                    :aria-pressed="feedbackRating === r"
                    @click="feedbackRating = r"
                  >
                    <span class="material-icons text-3xl" aria-hidden="true">{{ r <= feedbackRating ? 'star' : 'star_border' }}</span>
                  </button>
                </div>
                <p v-if="fieldErrors.rating" class="text-red-400 text-sm">{{ fieldErrors.rating }}</p>
                <div>
                  <label for="feedback-t3-name" class="block text-sm font-medium text-slate-400 mb-2">{{ t('public.yourName') }} <span class="text-red-400" aria-hidden="true">*</span></label>
                  <input
                    id="feedback-t3-name"
                    v-model="feedbackName"
                    type="text"
                    autocomplete="name"
                    maxlength="255"
                    class="w-full bg-t3-accent-dark border-none rounded-lg p-3 text-slate-100 focus:ring-2 focus:ring-t3-primary placeholder:text-slate-600 min-h-[44px]"
                    :aria-invalid="!!fieldErrors.name"
                    :aria-describedby="fieldErrors.name ? 'feedback-t3-name-error' : undefined"
                  >
                  <p id="feedback-t3-name-error" v-if="fieldErrors.name" class="text-red-400 text-sm mt-1">{{ fieldErrors.name }}</p>
                </div>
                <div>
                  <label for="feedback-t3-text" class="block text-sm font-medium text-slate-400 mb-2">{{ t('public.yourMessage') }} <span class="text-red-400" aria-hidden="true">*</span></label>
                  <textarea
                    id="feedback-t3-text"
                    v-model="feedbackText"
                    rows="4"
                    maxlength="65535"
                    class="w-full bg-t3-accent-dark border-none rounded-lg p-3 text-slate-100 focus:ring-2 focus:ring-t3-primary placeholder:text-slate-600 resize-none min-h-[120px]"
                    :placeholder="t('public.t3FeedbackPlaceholder')"
                    :aria-invalid="!!fieldErrors.text"
                    :aria-describedby="fieldErrors.text ? 'feedback-t3-text-error' : undefined"
                  />
                  <p id="feedback-t3-text-error" v-if="fieldErrors.text" class="text-red-400 text-sm mt-1">{{ fieldErrors.text }}</p>
                </div>
                <div v-if="recaptchaEnabled && !recaptchaIsV3" class="rms-feedback__recaptcha">
                  <div ref="recaptchaContainerRef" class="rms-feedback__recaptcha-widget" aria-label="reCAPTCHA" />
                  <p v-if="fieldErrors.captcha" class="text-red-400 text-sm mt-1" role="alert">{{ fieldErrors.captcha }}</p>
                </div>
                <button
                  type="submit"
                  class="w-full bg-t3-primary text-t3-bg-dark font-bold py-4 rounded-lg hover:bg-t3-primary/90 transition-colors uppercase tracking-widest text-sm min-h-[48px] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary"
                  :disabled="feedbackSubmitting"
                >
                  {{ feedbackSubmitting ? '…' : t('public.t3SubmitReview') }}
                </button>
              </form>
            </div>
          </section>
        </template>
      </Template3>
      <Template4
        v-else-if="templateComponent === 'Template4'"
        :restaurant="effectiveRestaurant"
        :owner-view-mode="ownerViewMode"
        :languages="data.languages || []"
        :current-locale="locale"
        @select-locale="onSelectLocale"
      >
        <template #feedback-form>
          <div id="feedback" class="rms-feedback rms-feedback--template-4" role="form" aria-labelledby="feedback-heading-t4">
            <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 sm:p-8 rounded-2xl shadow-lg">
              <p class="text-xs font-bold tracking-widest uppercase mb-2" style="color: var(--rms-t4-primary, #ec5b13)">{{ t('public.leaveFeedback') }}</p>
              <h3 id="feedback-heading-t4" class="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-1">{{ t('public.t3ShareExperience') }}</h3>
              <p class="text-slate-600 dark:text-slate-400 text-sm mb-6">{{ t('public.t3FeedbackHelps') }}</p>
              <form novalidate class="space-y-5" @submit.prevent="submitFeedback">
                <p v-if="feedbackError" class="text-red-600 dark:text-red-400 text-sm" role="alert">{{ feedbackError }}</p>
                <p v-if="feedbackSuccess" class="text-green-600 dark:text-green-400 text-sm" role="status">{{ feedbackSuccess }}</p>
                <div>
                  <p class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{{ t('public.ratingLabel') }}</p>
                  <div class="flex justify-center gap-1" role="group" :aria-label="t('public.ratingLabel')">
                    <button
                      v-for="r in 5"
                      :key="r"
                      type="button"
                      class="p-1.5 min-h-[44px] min-w-[44px] rounded-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                      :style="r <= feedbackRating ? { color: 'var(--rms-t4-primary, #ec5b13)' } : {}"
                      :class="r > feedbackRating ? 'text-slate-300 dark:text-slate-600 hover:text-slate-400 dark:hover:text-slate-500' : ''"
                      :aria-label="`${r} star${r > 1 ? 's' : ''}`"
                      :aria-pressed="feedbackRating === r"
                      @click="feedbackRating = r"
                    >
                      <span class="material-icons text-3xl block" aria-hidden="true">star</span>
                    </button>
                  </div>
                  <p v-if="fieldErrors.rating" class="text-red-600 dark:text-red-400 text-sm mt-1">{{ fieldErrors.rating }}</p>
                </div>
                <div>
                  <label for="feedback-t4-name" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{{ t('public.yourName') }} <span class="text-red-500" aria-hidden="true">*</span></label>
                  <input
                    id="feedback-t4-name"
                    v-model="feedbackName"
                    type="text"
                    autocomplete="name"
                    maxlength="255"
                    class="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-4 py-3 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:ring-2 focus:ring-[var(--rms-t4-primary,#ec5b13)] focus:border-[var(--rms-t4-primary,#ec5b13)] min-h-[44px] transition-colors duration-200"
                    :aria-invalid="!!fieldErrors.name"
                    :aria-describedby="fieldErrors.name ? 'feedback-t4-name-error' : undefined"
                  >
                  <p id="feedback-t4-name-error" v-if="fieldErrors.name" class="text-red-600 dark:text-red-400 text-sm mt-1">{{ fieldErrors.name }}</p>
                </div>
                <div>
                  <label for="feedback-t4-text" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{{ t('public.yourMessage') }} <span class="text-red-500" aria-hidden="true">*</span></label>
                  <textarea
                    id="feedback-t4-text"
                    v-model="feedbackText"
                    rows="4"
                    maxlength="65535"
                    class="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-4 py-3 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:ring-2 focus:ring-[var(--rms-t4-primary,#ec5b13)] focus:border-[var(--rms-t4-primary,#ec5b13)] resize-none min-h-[120px] transition-colors duration-200"
                    :placeholder="t('public.t3FeedbackPlaceholder')"
                    :aria-invalid="!!fieldErrors.text"
                    :aria-describedby="fieldErrors.text ? 'feedback-t4-text-error' : undefined"
                  />
                  <p id="feedback-t4-text-error" v-if="fieldErrors.text" class="text-red-600 dark:text-red-400 text-sm mt-1">{{ fieldErrors.text }}</p>
                </div>
                <div v-if="recaptchaEnabled && !recaptchaIsV3" class="rms-feedback__recaptcha">
                  <div ref="recaptchaContainerRef" class="rms-feedback__recaptcha-widget" aria-label="reCAPTCHA" />
                  <p v-if="fieldErrors.captcha" class="text-red-600 dark:text-red-400 text-sm mt-1" role="alert">{{ fieldErrors.captcha }}</p>
                </div>
                <button
                  type="submit"
                  class="w-full text-white font-bold py-3.5 rounded-xl min-h-[48px] disabled:opacity-60 transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer"
                  :style="{ backgroundColor: effectivePrimaryColor || '#ec5b13', '--tw-ring-color': 'var(--rms-t4-primary, #ec5b13)' }"
                  :disabled="feedbackSubmitting"
                >
                  {{ feedbackSubmitting ? '…' : t('public.t3SubmitReview') }}
                </button>
              </form>
            </div>
          </div>
        </template>
      </Template4>
      <Template5
        v-else-if="templateComponent === 'Template5'"
        :restaurant="effectiveRestaurant"
        :owner-view-mode="ownerViewMode"
        :languages="data.languages || []"
        :current-locale="locale"
        @select-locale="onSelectLocale"
      >
        <template #feedback-form>
          <div id="feedback" class="rms-feedback rms-feedback--template-5" role="form" aria-labelledby="feedback-heading-t5">
            <div class="rms-t5-feedback-card bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 sm:p-8 rounded-lg shadow-sm">
              <p class="font-condensed  font-bold uppercase tracking-wider mb-1.5" :style="{ color: 'var(--rms-t5-primary, #2563eb)' }">{{ t('public.leaveFeedback') }}</p>
              <h3 id="feedback-heading-t5" class="font-condensed text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-0.5">{{ t('public.t3ShareExperience') }}</h3>
              <p class="text-slate-600 dark:text-slate-400 text-sm mb-5">{{ t('public.t3FeedbackHelps') }}</p>
              <form novalidate class="space-y-4" @submit.prevent="submitFeedback">
                <p v-if="feedbackError" class="text-red-600 dark:text-red-400 text-xs" role="alert">{{ feedbackError }}</p>
                <p v-if="feedbackSuccess" class="text-green-600 dark:text-green-400 text-xs" role="status">{{ feedbackSuccess }}</p>
                <div>
                  <label class="block font-condensed text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5" :aria-label="t('public.ratingLabel')">{{ t('public.ratingLabel') }}</label>
                  <div class="flex justify-center gap-0.5" role="group" :aria-label="t('public.ratingLabel')">
                    <button
                      v-for="r in 5"
                      :key="r"
                      type="button"
                      class="p-1.5 min-h-[44px] min-w-[44px] rounded transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t5-primary,#2563eb)] focus-visible:ring-offset-2 cursor-pointer"
                      :style="r <= feedbackRating ? { color: 'var(--rms-t5-primary, #2563eb)' } : {}"
                      :class="r > feedbackRating ? 'text-slate-300 dark:text-slate-600 hover:text-slate-400 dark:hover:text-slate-500' : ''"
                      :aria-label="`${r} star${r > 1 ? 's' : ''}`"
                      :aria-pressed="feedbackRating === r"
                      @click="feedbackRating = r"
                    >
                      <span class="material-icons text-2xl sm:text-3xl block" aria-hidden="true">star</span>
                    </button>
                  </div>
                  <p v-if="fieldErrors.rating" class="text-red-600 dark:text-red-400 text-xs mt-1">{{ fieldErrors.rating }}</p>
                </div>
                <div>
                  <label for="feedback-t5-name" class="block font-condensed text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">{{ t('public.yourName') }} <span class="text-red-500 normal-case" aria-hidden="true">*</span></label>
                  <input
                    id="feedback-t5-name"
                    v-model="feedbackName"
                    type="text"
                    autocomplete="name"
                    maxlength="255"
                    class="w-full rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:ring-2 focus:ring-[var(--rms-t5-primary,#2563eb)] focus:border-[var(--rms-t5-primary,#2563eb)] min-h-[44px] transition-colors duration-200"
                    :aria-invalid="!!fieldErrors.name"
                    :aria-describedby="fieldErrors.name ? 'feedback-t5-name-error' : undefined"
                  >
                  <p id="feedback-t5-name-error" v-if="fieldErrors.name" class="text-red-600 dark:text-red-400 text-xs mt-1">{{ fieldErrors.name }}</p>
                </div>
                <div>
                  <label for="feedback-t5-text" class="block font-condensed text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">{{ t('public.yourMessage') }} <span class="text-red-500 normal-case" aria-hidden="true">*</span></label>
                  <textarea
                    id="feedback-t5-text"
                    v-model="feedbackText"
                    rows="4"
                    maxlength="65535"
                    class="w-full rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:ring-2 focus:ring-[var(--rms-t5-primary,#2563eb)] focus:border-[var(--rms-t5-primary,#2563eb)] resize-none min-h-[100px] transition-colors duration-200"
                    :placeholder="t('public.t3FeedbackPlaceholder')"
                    :aria-invalid="!!fieldErrors.text"
                    :aria-describedby="fieldErrors.text ? 'feedback-t5-text-error' : undefined"
                  />
                  <p id="feedback-t5-text-error" v-if="fieldErrors.text" class="text-red-600 dark:text-red-400 text-xs mt-1">{{ fieldErrors.text }}</p>
                </div>
                <div v-if="recaptchaEnabled && !recaptchaIsV3" class="rms-feedback__recaptcha">
                  <div ref="recaptchaContainerRef" class="rms-feedback__recaptcha-widget" aria-label="reCAPTCHA" />
                  <p v-if="fieldErrors.captcha" class="text-red-600 dark:text-red-400 text-xs mt-1" role="alert">{{ fieldErrors.captcha }}</p>
                </div>
                <button
                  type="submit"
                  class="w-full text-white font-bold py-3 rounded text-xs uppercase tracking-wider min-h-[44px] disabled:opacity-60 transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t5-primary,#2563eb)] focus-visible:ring-offset-2 cursor-pointer"
                  :style="{ backgroundColor: effectivePrimaryColor || '#2563eb', '--tw-ring-color': 'var(--rms-t5-primary, #2563eb)' }"
                  :disabled="feedbackSubmitting"
                >
                  {{ feedbackSubmitting ? '…' : t('public.t3SubmitReview') }}
                </button>
              </form>
            </div>
          </div>
        </template>
      </Template5>

      <!-- Spacer so content is not hidden behind owner sticky bar -->
      <div
        v-if="showOwnerStickyBar || (data.viewer?.is_owner && viewAsCustomer)"
        class="rms-owner-sticky-spacer"
        aria-hidden="true"
      />
      <!-- Owner-only: sticky bar "You're viewing as owner" + View as customer (hidden when not logged in or when viewing as customer) -->
      <div
        v-if="showOwnerStickyBar"
        class="rms-owner-sticky"
        role="status"
        aria-live="polite"
      >
        <span class="rms-owner-sticky__badge" aria-hidden="true">
          <span class="material-symbols-outlined rms-owner-sticky__icon" aria-hidden="true">visibility</span>
          {{ t('public.viewingAsOwner') }}
        </span>
        <button
          type="button"
          class="rms-owner-sticky__btn"
          :style="stickyButtonStyle"
          @click="viewAsCustomer = true"
        >
          <span class="material-symbols-outlined rms-owner-sticky__btn-icon" aria-hidden="true">person</span>
          {{ t('public.viewAsCustomer') }}
        </button>
      </div>
      <!-- When owner is in "view as customer" mode, show a minimal pill to switch back -->
      <div
        v-else-if="data.viewer?.is_owner && viewAsCustomer"
        class="rms-owner-sticky rms-owner-sticky--minimal"
      >
        <button
          type="button"
          class="rms-owner-sticky__link"
          :style="stickyLinkStyle"
          @click="viewAsCustomer = false"
        >
          <span class="material-symbols-outlined rms-owner-sticky__link-icon" aria-hidden="true">edit</span>
          {{ t('public.viewAsOwner') }}
        </button>
      </div>

      <!-- Mobile-only: spacer so sticky "View Menu" bar does not cover footer -->
      <div class="rms-view-menu-spacer" aria-hidden="true" />

      <!-- Mobile-only: sticky "View Menu" / "View Services" / "View catalog" button (label depends on business type) -->
      <div class="rms-view-menu-bar">
        <button
          ref="viewMenuBtnRef"
          type="button"
          class="rms-view-menu-bar__btn"
          :style="stickyButtonStyle"
          :aria-label="viewListAriaLabel || t('public.ariaViewMenu')"
          @click="openMenuModalIfAllowed"
        >
          {{ viewListButtonLabel || t('public.viewMenu') }}
        </button>
      </div>

      <PublicMenuModal
        v-model="menuModalOpen"
        :restaurant="effectiveRestaurant"
        :show-owner-edit-fab="ownerViewMode"
      />

      <!-- Quick Order: FAB + Sheet (only when viewing as customer: guest or owner in "view as customer" mode) -->
      <div data-testid="qo-debug" :data-qo-available="String(quickOrderAvailable)" :data-qo-cart="String(!!cart)" :data-qo-cart-count="cart ? String(cart.itemCount.value) : 'null'" style="display:none" />
      <template v-if="viewingAsCustomer && quickOrderAvailable && cart">
        <QuickOrderCartFab
          :item-count="cart.itemCount.value"
          :primary-color="effectivePrimaryColor"
          @open="cartSheetOpen = true"
        />
        <QuickOrderCartSheet
          v-model="cartSheetOpen"
          :items="cart.items.value"
          :slug="data.slug"
          :quick-order-phone="quickOrderPhone"
          :checkout-only="data.quick_order_checkout_only === true"
          :primary-color="effectivePrimaryColor"
          :currency="data.currency || 'USD'"
          :show-currency="data.show_currency !== false"
          @clear-cart="cart.clearCart()"
          @remove-item="(uuid, variantUuid, modifierUuids) => cart.removeItem(uuid, variantUuid, modifierUuids)"
          @update-quantity="(uuid, qty, variantUuid, modifierUuids) => cart.updateQuantity(uuid, qty, variantUuid, modifierUuids)"
          @order-sent="onQuickOrderSent"
        />
      </template>

      <!-- Owner viewing as owner: Theme FAB + modal (primary color preview + template; Save persists, template change reloads) -->
      <PublicThemeFab
        v-if="showOwnerStickyBar"
        v-model="themeFabOpen"
        :restaurant="data"
        @preview-color="previewPrimaryColor = $event"
        @save="onPublicThemeSave"
        @close="previewPrimaryColor = null"
      />
      <PublicOwnerLogoBannerModal
        v-if="data"
        v-model="showOwnerMediaModal"
        :restaurant-uuid="data.uuid || ''"
        :logo-url="data.logo_url || ''"
        :banner-url="data.banner_url || ''"
        @updated="onOwnerMediaUpdated"
      />
      <PublicAddItemFab
        v-if="data && showOwnerStickyBar"
        :restaurant="effectiveRestaurant"
        @item-added="onQuickItemAdded"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, watch, watchEffect, nextTick, onMounted, onBeforeUnmount, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { i18n } from '@/i18n'
import { restaurantService, feedbackService, normalizeApiError } from '@/services'
import { useAppStore } from '@/stores/app'
import { useToastStore } from '@/stores/toast'
import { useRecaptcha } from '@/composables/useRecaptcha'
import { useQuickOrderCart } from '@/composables/useQuickOrderCart'
import { useBusinessTypeLabels } from '@/composables/useBusinessTypeLabels'
import { RMS_OWNER_LOGO_MODAL_KEY } from '@/composables/usePublicOwnerLogoModal'
import PublicOwnerLogoBannerModal from '@/components/public/PublicOwnerLogoBannerModal.vue'
import PublicRestaurant from '@/models/PublicRestaurant'
import { mergeLegacyPublicContacts } from '@/utils/publicRestaurantContacts'
import Template1 from '@/components/public/templates/Template1.vue'
import Template2 from '@/components/public/templates/Template2.vue'
import Template3 from '@/components/public/templates/Template3.vue'
import Template4 from '@/components/public/templates/Template4.vue'
import Template5 from '@/components/public/templates/Template5.vue'
import PublicMenuModal from '@/components/public/PublicMenuModal.vue'
import PublicReservationTrigger from '@/components/public/PublicReservationTrigger.vue'
import QuickOrderCartFab from '@/components/public/QuickOrderCartFab.vue'
import QuickOrderCartSheet from '@/components/public/QuickOrderCartSheet.vue'
import PublicThemeFab from '@/components/public/PublicThemeFab.vue'
import PublicAddItemFab from '@/components/public/PublicAddItemFab.vue'

// Load template CSS (same class names as Blade)
import '@/assets/public-restaurant-templates.css'

const props = defineProps({
  slug: { type: String, default: '' },
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()

/** Supported locale codes for public templates (fallback to en if unknown). Must match i18n messages and config/locales.js. */
const SUPPORTED_PUBLIC_LOCALES = ['en', 'es', 'ar', 'zh', 'fil', 'de', 'fr', 'uk', 'ru', 'ja', 'nl']

/** Use only URL ?locale= so the first API call uses no locale param and the API returns the restaurant's default_locale and its description. */
function getInitialLocale() {
  return (route.query.locale ?? '').trim()
}

const slug = computed(() => props.slug || route.params.slug || '')
const locale = ref(getInitialLocale())
const loading = ref(true)
const error = ref(null)
const data = ref(null)
const toastStore = useToastStore()

/** Labels for sticky "View Menu" button and elsewhere — depend on business type / template. */
const { viewListButtonLabel, viewListAriaLabel } = useBusinessTypeLabels(() => data.value, t)

// Quick Order cart — slug is reactive so we init lazily; use a stable ref.
const cartSlug = computed(() => data.value?.slug || slug.value || '')
const cart = shallowRef(null)
const cartSheetOpen = ref(false)

function ensureCart() {
  const s = cartSlug.value
  if (!s) return
  if (!cart.value || cart.value._slug !== s) {
    const c = useQuickOrderCart(s)
    c._slug = s
    cart.value = c
  }
}

const quickOrderAvailable = computed(() => data.value?.quick_order_available === true)
const quickOrderPhone = computed(() => data.value?.quick_order_phone ?? null)

provide('quickOrderCart', computed(() => cart.value))
provide('quickOrderAvailable', quickOrderAvailable)

/** Guard so only one of menu modal or reservation modal is open at a time. 'menu' | 'reservation' | null. */
const activePublicModal = ref(null)
provide('activePublicModal', activePublicModal)

const { recaptchaEnabled, recaptchaIsV3, loadScript, renderWidget: renderRecaptcha, getToken: getRecaptchaToken, executeToken: executeRecaptchaToken, reset: resetRecaptcha } = useRecaptcha()
const recaptchaContainerRef = ref(null)

const feedbackRating = ref(0)
const feedbackName = ref('')
const feedbackText = ref('')
const fieldErrors = ref({})
const feedbackError = ref('')
const feedbackSuccess = ref('')
const feedbackSubmitting = ref(false)

const DEFAULT_ACCENT = '#e85a2b'

/** Default document title (from index.html). Restored when leaving public page or on error. */
const DEFAULT_DOCUMENT_TITLE = 'QRestoo'
/** App name for public page title: "[Restaurant name] | [App name]". Configurable via VITE_APP_NAME. */
const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'RMS'

/** Landing page (main site) URL for "Back to home" on restaurant not found. Uses VITE_APP_URL so subdomain users go to main domain. */
const landingPageUrl = (() => {
  // Get the main domain (no subdomain) from a URL.
  // Example: "https://demo.rms.local" -> "rms.local"
  function getMainDomain(url = window.location.origin) {
    try {
      // Ensure url includes protocol for URL parsing
      const dummy = url.match(/^https?:\/\//) ? url : `https://${url}`;
      const { protocol, hostname } = new URL(dummy);
      const parts = hostname.split('.');
      if (parts.length < 2) return `${protocol}//${hostname}`;
      // Always return the last two parts as main domain, e.g. "https://rms.local"
      return `${protocol}//${parts.slice(-2).join('.')}`;
    } catch (e) {
      console.error('Error getting main domain', e)
      return window.location.origin;
    }
  }
 
  const base = getMainDomain(window.location.origin)
  return (base || '').replace(/\/$/, '') || '/'
})()

const menuModalOpen = ref(false)
/** Only auto-open View Menu modal once per page load on mobile. */
const hasAutoOpenedModal = ref(false)
/** Track menu_view only once per page load so refetches don't double-count. */
const hasTrackedMenuView = ref(false)
/** When true, the announcement dialog is dismissed for this page visit. */
const announcementDismissed = ref(false)
/** When true, user chose "Don't show again for 4 days" before closing. */
const hideAnnouncementForFourDays = ref(false)

const ANNOUNCEMENT_HIDE_STORAGE_PREFIX = 'rms-announcement-hide-until-'
const FOUR_DAYS_MS = 4 * 24 * 60 * 60 * 1000

function closeAnnouncementDialog() {
  if (hideAnnouncementForFourDays.value && data.value?.slug) {
    try {
      const hideUntil = Date.now() + FOUR_DAYS_MS
      localStorage.setItem(ANNOUNCEMENT_HIDE_STORAGE_PREFIX + data.value.slug, String(hideUntil))
    } catch (_) {}
  }
  announcementDismissed.value = true
  hideAnnouncementForFourDays.value = false
}

function isAnnouncementHiddenForFourDays() {
  const slug = data.value?.slug
  if (!slug) return false
  try {
    const stored = localStorage.getItem(ANNOUNCEMENT_HIDE_STORAGE_PREFIX + slug)
    if (!stored) return false
    const hideUntil = parseInt(stored, 10)
    return Number.isFinite(hideUntil) && Date.now() < hideUntil
  } catch (_) {
    return false
  }
}
/** When true, owner sees the page as a customer (no owner notice, no sticky bar). */
const viewAsCustomer = ref(false)
/** Owner view mode: show owner-only UI (needs-data notice, sticky). When false, hide owner-only UI. */
const ownerViewMode = computed(() => !!(
  data.value?.viewer?.is_owner === true && !viewAsCustomer.value
))
/** Show the sticky "view as customer" bar only when owner is viewing and not in customer mode. Guests never see it. */
const showOwnerStickyBar = computed(() => !!(
  data.value?.viewer?.is_owner === true && !viewAsCustomer.value
))

/** Let global UI (e.g. SitePreferencesBar) clear fixed owner bars on this page. */
watchEffect(() => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const isOwner = data.value?.viewer?.is_owner === true
  if (!isOwner) {
    root.removeAttribute('data-rms-public-owner-ui')
    return
  }
  if (showOwnerStickyBar.value) {
    root.setAttribute('data-rms-public-owner-ui', 'sticky')
  } else if (viewAsCustomer.value) {
    root.setAttribute('data-rms-public-owner-ui', 'minimal')
  } else {
    root.removeAttribute('data-rms-public-owner-ui')
  }
})

/** Owner preview: open logo/banner modal on the public page (no redirect). */
const showOwnerMediaModal = ref(false)
provide(RMS_OWNER_LOGO_MODAL_KEY, {
  enabled: computed(() => data.value?.viewer?.is_owner === true && !viewAsCustomer.value && !!data.value?.uuid),
  open: () => {
    if (data.value?.viewer?.is_owner !== true || viewAsCustomer.value || !data.value?.uuid) return
    showOwnerMediaModal.value = true
  },
})

function onOwnerMediaUpdated(payload) {
  if (!data.value || !payload || typeof payload !== 'object') return
  if (payload.logo_url !== undefined) data.value.logo_url = payload.logo_url ?? ''
  if (payload.banner_url !== undefined) data.value.banner_url = payload.banner_url ?? ''
  applyPublicPageSeo(data.value)
}

/** Refresh public menu after owner quick-add (templates + View menu modal) without full-page reload. */
async function onQuickItemAdded() {
  if (data.value?.uuid) {
    restaurantService.invalidateMenuItemsCache(data.value.uuid)
  }
  await fetchRestaurant({ soft: true })
}

/** Truncate description for meta (SEO best practice ~155 chars). */
function truncateForMeta(text, maxLen = 155) {
  if (!text || typeof text !== 'string') return ''
  const t = text.trim()
  if (t.length <= maxLen) return t
  return t.slice(0, maxLen - 1).trimEnd() + '…'
}

/** Ensure meta element exists (by name or property), set content, mark for cleanup. */
function setMeta(nameOrProperty, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name'
  let el = document.querySelector(`meta[${attr}="${nameOrProperty}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, nameOrProperty)
    el.setAttribute('data-rms-seo', '')
    document.head.appendChild(el)
  }
  el.setAttribute('content', content || '')
}

const DEFAULT_VIEWPORT = 'width=device-width, initial-scale=1.0'
const PUBLIC_PAGE_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'

/** Remove all meta tags we added for public page SEO. */
function removeSeoMeta() {
  document.querySelectorAll('meta[data-rms-seo]').forEach((el) => el.remove())
}

function setViewport(content) {
  const el = document.querySelector('meta[name="viewport"]')
  if (el) el.setAttribute('content', content)
}

/** Apply SEO title and meta from restaurant data. Disable zoom on mobile for public restaurant page. */
function applyPublicPageSeo(restaurant) {
  if (!restaurant?.name) return
  setViewport(PUBLIC_PAGE_VIEWPORT)
  const title = `${restaurant.name} | ${APP_NAME}`
  document.title = title
  const description = truncateForMeta(restaurant.description)
  if (description) setMeta('description', description)
  setMeta('og:title', title, true)
  setMeta('og:description', description, true)
  setMeta('og:type', 'website', true)
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : ''
  if (canonicalUrl) setMeta('og:url', canonicalUrl, true)
  const image = restaurant.banner_url || restaurant.logo_url
  if (image) setMeta('og:image', image, true)
  setMeta('twitter:card', image ? 'summary_large_image' : 'summary')
  setMeta('twitter:title', title)
  setMeta('twitter:description', description)
  if (image) setMeta('twitter:image', image)
}

/** Restore default title, viewport, and remove our meta tags. */
function clearPublicPageSeo() {
  document.title = DEFAULT_DOCUMENT_TITLE
  setViewport(DEFAULT_VIEWPORT)
  removeSeoMeta()
}
const viewMenuBtnRef = ref(null)
const themeFabOpen = ref(false)
/** When owner opens Theme FAB and changes color, this is set for live preview; cleared on save or close. */
const previewPrimaryColor = ref(null)

/** Primary color used across the page; includes live preview when owner is editing theme. */
const effectivePrimaryColor = computed(() => {
  const preview = previewPrimaryColor.value
  if (preview !== null && preview !== undefined) return preview || DEFAULT_ACCENT
  return data.value?.primary_color || DEFAULT_ACCENT
})

/** Restaurant data with primary_color overridden for live theme preview. */
const effectiveRestaurant = computed(() => {
  const d = data.value
  if (!d) return d
  return {
    ...d,
    primary_color: effectivePrimaryColor.value,
    contacts: mergeLegacyPublicContacts(d),
  }
})

/** True when the page is in "customer" view: guest or owner who clicked "View as customer". Quick Order FAB and cart only show in this mode. */
const viewingAsCustomer = computed(() => viewAsCustomer.value || !data.value?.viewer?.is_owner)

const wrapperStyle = computed(() => ({
  '--rms-accent': effectivePrimaryColor.value,
}))

/** Announcement dialog: show when there is an active message, user has not dismissed, and "hide for 4 days" is not set. */
const showAnnouncementDialog = computed(() => {
  const ann = data.value?.active_announcement
  const hasContent = ann && ((ann.message || '').trim() || (ann.image_url || '').trim())
  if (!hasContent || announcementDismissed.value) return false
  return !isAnnouncementHiddenForFourDays()
})

/** Primary color for announcement accent bar and button. */
const announcementStyle = computed(() => ({
  backgroundColor: effectivePrimaryColor.value,
}))

/** Dialog card border uses primary. */
const announcementDialogStyle = computed(() => ({
  '--rms-announcement-accent': effectivePrimaryColor.value,
}))

/** Plain text for announcement message (strips HTML for legacy WYSIWYG content). */
const announcementPlainMessage = computed(() => {
  const msg = data.value?.active_announcement?.message
  if (msg == null || msg === '') return ''
  const div = typeof document !== 'undefined' ? document.createElement('div') : null
  if (div) {
    div.innerHTML = msg
    return div.textContent?.trim() ?? msg
  }
  return String(msg).replace(/<[^>]*>/g, '').trim()
})

/** Close button uses primary background. */
const announcementButtonStyle = computed(() => ({
  backgroundColor: effectivePrimaryColor.value,
}))

const stickyButtonStyle = computed(() => ({
  backgroundColor: effectivePrimaryColor.value,
}))

/** Primary background + white text for the "View as owner" minimal link. */
const stickyLinkStyle = computed(() => ({
  backgroundColor: effectivePrimaryColor.value,
  color: '#fff',
}))

/** template-1 / template-2 / template-3 / template-4 / template-5; same as Blade resolveTemplate. */
const templateClass = computed(() => {
  const t = data.value?.template ?? 'template-1'
  if (t === 'template-2' || t === 'minimal') return 'rms-template-2'
  if (t === 'template-3') return 'rms-template-3'
  if (t === 'template-4') return 'rms-template-4'
  if (t === 'template-5') return 'rms-template-5'
  return 'rms-template-1'
})

/** Which template component to render (Template1 | Template2 | Template3 | Template4 | Template5). */
const templateComponent = computed(() => {
  const t = data.value?.template ?? 'template-1'
  if (t === 'template-2' || t === 'minimal') return 'Template2'
  if (t === 'template-3') return 'Template3'
  if (t === 'template-4') return 'Template4'
  if (t === 'template-5') return 'Template5'
  return 'Template1'
})
let fetchId = 0
/**
 * @param {{ soft?: boolean }} [options] - soft: refetch without full-page loader or clearing data (e.g. after owner quick-add item).
 */
async function fetchRestaurant(options = {}) {
  const soft = options.soft === true
  if (!slug.value) {
    error.value = 'Missing restaurant slug.'
    loading.value = false
    return
  }
  const thisFetchId = ++fetchId
  if (!soft) {
    loading.value = true
    error.value = null
    data.value = null
  }
  try {
    const params = locale.value ? { locale: locale.value } : {}
    const res = await restaurantService.getPublicRestaurant(slug.value, params)
    if (thisFetchId !== fetchId) return
    data.value = PublicRestaurant.fromApi(res).toJSON()
    if (data.value) {
      locale.value = data.value.locale ?? data.value.default_locale ?? locale.value ?? 'en'
    }
  } catch (e) {
    if (thisFetchId !== fetchId) return
    if (soft) {
      toastStore.error(normalizeApiError(e).message ?? t('public.restaurantNotFoundTitle'))
    } else if (e?.response?.status === 404) {
      error.value = 'This restaurant does not exist or the link is wrong.'
    } else {
      error.value = normalizeApiError(e).message ?? 'Failed to load restaurant.'
    }
  } finally {
    if (thisFetchId === fetchId && !soft) loading.value = false
  }
}

function onSelectLocale(code) {
  if (!code || typeof code !== 'string') return
  locale.value = code
  router.replace({
    name: route.name,
    params: route.params,
    query: { ...route.query, locale: code },
  })
  fetchRestaurant()
}

function validateFeedbackForm() {
  const err = {}
  const r = feedbackRating.value
  if (r == null || Number(r) < 1 || Number(r) > 5) {
    err.rating = 'Please choose a rating from 1 to 5.'
  }
  const name = (feedbackName.value ?? '').trim()
  if (!name) err.name = 'Your name is required.'
  else if (name.length > 255) err.name = 'Name must be at most 255 characters.'
  const text = (feedbackText.value ?? '').trim()
  if (!text) err.text = 'Your message is required.'
  else if (text.length > 65535) err.text = 'Message is too long.'
  if (recaptchaEnabled.value && !recaptchaIsV3.value && !getRecaptchaToken()) {
    err.captcha = t('public.captchaRequired')
  }
  fieldErrors.value = err
  return Object.keys(err).length === 0
}

async function submitFeedback() {
  feedbackError.value = ''
  feedbackSuccess.value = ''
  fieldErrors.value = {}
  if (!validateFeedbackForm()) return
  feedbackSubmitting.value = true
  try {
    const payload = {
      rating: Number(feedbackRating.value),
      text: (feedbackText.value ?? '').trim(),
      name: (feedbackName.value ?? '').trim(),
    }
    if (recaptchaEnabled.value) {
      const token = recaptchaIsV3.value
        ? await executeRecaptchaToken('feedback')
        : getRecaptchaToken()
      if (!token) {
        fieldErrors.value = { ...fieldErrors.value, captcha: t('public.captchaRequired') }
        feedbackError.value = t('public.captchaRequired')
        feedbackSubmitting.value = false
        return
      }
      payload.captcha_token = token
    }
    const res = await feedbackService.submitFeedback(slug.value, payload)
    feedbackSuccess.value = res?.message ?? 'Thank you for your feedback.'
    feedbackRating.value = 0
    feedbackName.value = ''
    feedbackText.value = ''
    if (recaptchaEnabled.value) resetRecaptcha()
  } catch (e) {
    const normalized = normalizeApiError(e)
    if (e?.response?.status === 429) {
      feedbackError.value = 'Too many submissions. Please try again in a minute.'
    } else if (e?.response?.status === 422 && e?.response?.data?.errors) {
      const errors = e.response.data.errors
      const err = {}
      if (errors.rating?.[0]) err.rating = errors.rating[0]
      if (errors.name?.[0]) err.name = errors.name[0]
      if (errors.text?.[0]) err.text = errors.text[0]
      if (errors.captcha_token?.[0]) {
        err.captcha = errors.captcha_token[0]
        if (recaptchaEnabled.value) resetRecaptcha()
      }
      fieldErrors.value = err
      const msg = (normalized?.message && String(normalized.message).trim()) || ''
      feedbackError.value = msg || t('public.formErrorGeneric')
    } else {
      const msg = (normalized?.message && String(normalized.message).trim()) || ''
      feedbackError.value = msg || t('public.formErrorGeneric')
    }
  } finally {
    feedbackSubmitting.value = false
  }
}

watch(slug, () => {
  const fromQuery = route.query.locale ?? ''
  if (fromQuery) locale.value = fromQuery
  fetchRestaurant()
}, { immediate: true })

// Refetch when auth becomes available so viewer.is_owner is set (token is in-memory; first fetch may run before bootstrapAuth).
// Use slug.value (from route/props) so we refetch even when the first fetch hasn't completed yet (data still null).
watch(() => [appStore.user, appStore.isAuthBootstrapped], ([user, bootstrapped]) => {
  if (!bootstrapped || !user || !slug.value) return
  if (data.value?.viewer?.is_owner === true) return
  fetchRestaurant()
}, { immediate: true })

function openMenuModalIfAllowed() {
  if (activePublicModal.value === 'reservation') return
  activePublicModal.value = 'menu'
  menuModalOpen.value = true
}

watch(menuModalOpen, (open) => {
  if (!open) {
    if (activePublicModal.value === 'menu') activePublicModal.value = null
    nextTick(() => viewMenuBtnRef.value?.focus())
  }
})

async function onPublicThemeSave({ primary_color, template }) {
  const uuid = data.value?.uuid
  if (!uuid) return
  const currentTemplate = data.value?.template ?? 'template-1'
  try {
    const res = await restaurantService.update(uuid, { primary_color, template })
    const updated = res?.data
    if (template !== currentTemplate) {
      window.location.reload()
      return
    }
    if (updated) {
      if (updated.primary_color !== undefined) data.value.primary_color = updated.primary_color
      if (updated.template !== undefined) data.value.template = updated.template
    }
    previewPrimaryColor.value = null
    toastStore.success(t('app.primaryColorUpdated'))
  } catch (e) {
    toastStore.error(normalizeApiError(e).message ?? t('app.errorSaving'))
  }
}

function onQuickOrderSent(payload) {
  if (cart.value) cart.value.clearCart()
  if (payload?.showToast !== false) {
    toastStore.success(t('public.quickOrder.orderSent'))
  }
}

watch(data, (val) => {
  if (val?.name) {
    applyPublicPageSeo(val)
  } else {
    clearPublicPageSeo()
  }
  // Public analytics: one menu_view per page load (data ready for all restaurants, including non-Pro).
  if (val?.slug && !hasTrackedMenuView.value) {
    hasTrackedMenuView.value = true
    restaurantService.trackPublicAnalytics(val.slug, 'menu_view').catch(() => {})
  }
  // Init Quick Order cart when data is available
  if (val?.slug) {
    ensureCart()
    if (cart.value) {
      const seen = new Set()
      const allItems = []
      function pushMenuItem(it) {
        const id = it?.uuid
        if (!id || seen.has(id)) return
        seen.add(id)
        allItems.push(it)
      }
      for (const g of val.menu_groups ?? []) {
        if (Array.isArray(g.items)) g.items.forEach(pushMenuItem)
      }
      for (const it of val.menu_items ?? []) pushMenuItem(it)
      if (allItems.length) cart.value.validateCart(allItems)
    }
  }
  // Sync i18n locale so public template static content (About, Contact Us, etc.) uses the current language.
  const loc = val?.locale ?? locale.value
  if (loc && SUPPORTED_PUBLIC_LOCALES.includes(loc)) {
    i18n.global.locale.value = loc
  }
  // On mobile, auto-open View Menu modal once when restaurant data has loaded.
  // Skip when the business owner is viewing their own site — the wizard FAB and owner bar are more relevant.
  if (val && !hasAutoOpenedModal.value && !showOwnerStickyBar.value && typeof window !== 'undefined' && window.innerWidth < 768) {
    hasAutoOpenedModal.value = true
    if (activePublicModal.value !== 'reservation') {
      activePublicModal.value = 'menu'
      menuModalOpen.value = true
    }
  }
}, { immediate: true })

// When restaurant data is loaded and reCAPTCHA is enabled: v2 = render checkbox; v3 = load script only (token on submit).
watch(data, (val) => {
  if (!val || !recaptchaEnabled.value) return
  if (recaptchaIsV3.value) {
    loadScript().catch(() => {})
    return
  }
  nextTick(() => {
    nextTick(() => {
      const container = recaptchaContainerRef.value
      if (container) {
        renderRecaptcha(container).catch(() => {})
      }
    })
  })
}, { immediate: true })

const PUBLIC_PAGE_HTML_CLASS = 'rms-public-page'

onMounted(() => {
  document.documentElement.classList.add(PUBLIC_PAGE_HTML_CLASS)
})

onBeforeUnmount(() => {
  clearPublicPageSeo()
  document.documentElement.classList.remove(PUBLIC_PAGE_HTML_CLASS)
  document.documentElement.removeAttribute('data-rms-public-owner-ui')
})
</script>

<style scoped>
.rms-public__loading,
.rms-public__error {
  min-height: 100vh;
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

/* Brand primary for error/loading when no restaurant data (no --rms-accent). */
.rms-public__error {
  --rms-error-accent: #e85a2b;
}

.rms-public__spinner {
  font-size: 2.5rem;
  color: var(--rms-accent, #e85a2b);
  animation: rms-spin 1s linear infinite;
}

.rms-public__loading-text {
  margin-top: 0.75rem;
  color: #64748b;
}

.rms-public__error-card {
  max-width: 28rem;
  width: 100%;
  padding: 2rem 1.5rem;
  text-align: center;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(232, 90, 43, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(232, 90, 43, 0.2);
}

@media (prefers-color-scheme: dark) {
  .rms-public__error-card {
    background: #27272a;
    border-color: rgba(232, 90, 43, 0.25);
    box-shadow: 0 4px 20px rgba(232, 90, 43, 0.12), 0 2px 8px rgba(0, 0, 0, 0.2);
  }
}

.rms-public__error-icon {
  display: block;
  font-size: 3rem;
  color: var(--rms-error-accent, #e85a2b);
  line-height: 1;
}

.rms-public__error-title {
  margin-top: 1.25rem;
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
}

.dark .rms-public__error-title {
  color: #fff;
}

.rms-public__error-message {
  margin-top: 0.625rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #fff;
}

.dark .rms-public__error-message {
  color: #94a3b8;
}

/* Solid primary CTA button (Back to home) */
.rms-public__error-cta {
  margin-top: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  min-height: 44px;
  min-width: 44px;
  border-radius: 0.5rem;
  background: var(--rms-error-accent, #e85a2b);
  border: none;
  transition: opacity 0.2s ease;
}

.rms-public__error-cta:hover {
  opacity: 0.9;
}

.rms-public__error-cta:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--rms-error-accent, #e85a2b);
}

.rms-public__error-cta-icon {
  font-size: 1.25rem;
}

@keyframes rms-spin {
  to { transform: rotate(360deg); }
}

.rms-feedback__recaptcha-widget {
  min-height: 78px;
}

.rms-opening-hours {
  list-style: none;
  padding: 0;
  margin: 0;
}

.rms-opening-hours__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.25rem 0;
  min-height: 44px;
  align-items: center;
}

.rms-opening-hours__day {
  font-weight: 600;
  color: #0f172a;
  min-width: 6rem;
}

.rms-opening-hours__slots {
  color: #475569;
}

/* Mobile-only: spacer so fixed bar doesn't cover footer (height matches bar) */
.rms-view-menu-spacer {
  display: block;
  height: 56px;
  flex-shrink: 0;
}
@media (min-width: 768px) {
  .rms-view-menu-spacer {
    display: none;
  }
}

.rms-owner-sticky-spacer {
  height: 56px;
  flex-shrink: 0;
}
@media (min-width: 768px) {
  .rms-owner-sticky-spacer {
    height: 0;
  }
}

/* Owner-only: sticky bar "view as customer" (hidden for guests) — clean, professional */
.rms-owner-sticky {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  padding: 0.625rem 1rem;
  padding-bottom: max(0.625rem, env(safe-area-inset-bottom));
  background: #0f172a;
  color: #f8fafc;
  border-top: 1px solid #334155;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
}
@media (min-width: 768px) {
  .rms-owner-sticky {
    padding: 0.75rem 1.5rem;
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
    border-radius: 12px 12px 0 0;
    left: 50%;
    transform: translateX(-50%);
    max-width: min(640px, calc(100% - 2rem));
    border-left: 1px solid #334155;
    border-right: 1px solid #334155;
  }
}
@media (max-width: 767px) {
  .rms-owner-sticky {
    bottom: 60px;
  }
}
.rms-owner-sticky__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
}
.rms-owner-sticky__icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
  color: #94a3b8;
  flex-shrink: 0;
}
.rms-owner-sticky__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  flex-shrink: 0;
  min-height: 44px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
}
@media (prefers-reduced-motion: reduce) {
  .rms-owner-sticky__btn {
    transition: none;
  }
}
.rms-owner-sticky__btn:hover {
  opacity: 0.95;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.rms-owner-sticky__btn:focus-visible {
  outline: 2px solid #f8fafc;
  outline-offset: 2px;
}
.rms-owner-sticky__btn-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
.rms-owner-sticky--minimal {
  justify-content: center;
  padding: 0.5rem 1rem;
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
}
@media (min-width: 768px) {
  .rms-owner-sticky--minimal {
    border-radius: 12px 12px 0 0;
    left: 50%;
    transform: translateX(-50%);
    max-width: min(320px, calc(100% - 2rem));
  }
}
.rms-owner-sticky__link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
}
@media (prefers-reduced-motion: reduce) {
  .rms-owner-sticky__link {
    transition: none;
  }
}
.rms-owner-sticky__link:hover {
  opacity: 0.95;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.rms-owner-sticky__link:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.rms-owner-sticky__link-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: inherit;
}

/* Mobile-only: sticky "View Menu" bar */
.rms-view-menu-bar {
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0.5rem 1rem;
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}
@media (min-width: 768px) {
  .rms-view-menu-bar {
    display: none;
  }
}

.rms-view-menu-bar__btn {
  width: 100%;
  min-height: 44px;
  padding: 0.625rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;
}
.rms-view-menu-bar__btn:hover {
  opacity: 0.9;
}
.rms-view-menu-bar__btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
